import React, { useState } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import { genericPost } from '../../../services/authServices';
import { CREATE_IMPORT_PLANNING_BUDGET, } from '../../../services/apiRoutes';
import CustomAutocomplete from "../../common/CustomAutoComplete";
import CustomStyle from "../../common/CustomStyle";
import CustomPrefixSuffixField from "../../common/CustomPrefixSuffixField";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    seaFreight: Yup.string().required('Sea Freight is required'),
    currencyId: Yup.number().required('Currency is required'),
    cfsLocalCharges: Yup.number().required('CFS Local Charges are required'),
    shippingLineCharges: Yup.number().required('Shipping Line Charges are required'),
    blCharges: Yup.number().required('BL Charges are required'),
});

const CreatePlanningBudgetImport = ({ handleClose, setMessage, fetchData, proformaInvoiceList, commercialInvoiceList, fetchButtonList, shipmentOrgId, baseCurrencyId, currencySym, currencySymbol, currencyId, currencyList, clientCompanyId, commanShipmentId, shipmentId }) => {

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                shipmentOrgId: values.shipmentOrgId,
                commanShipmentId: values.commanShipmentId,
                shipmentId: values.shipmentId,
                clientCompanyId: values.clientCompanyId,
                currencyId: values.currencyId,
                baseCurrencyId: values.baseCurrencyId,
                conversionRate: values.conversionRate,
                proformaInvoiceId: values.proformaInvoiceId,
                proformaInvExpId: values.proformaInvExpId,
                commercialInvExpId: values.commercialInvExpId,
                commercialInvoiceId: values.commercialInvoiceId,
                seaFreight: values.seaFreight,
                clearingAgencyCharge: values.clearingAgencyCharge,
                freightForwardAgencyCharge: values.freightForwardAgencyCharge,
                cfsLocalCharges: values.cfsLocalCharges,
                shippingLineCharges: values.shippingLineCharges,
                blCharges: values.blCharges,
                othersCharges: values.othersCharges,
                dutyCharge: values.dutyCharge,
                taxCharge: values.taxCharge,
                transportCharge: values.transportCharge
            };

            const response = await genericPost(CREATE_IMPORT_PLANNING_BUDGET, payload);

            if (response.status === 201) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "planning created successfully",
                    type: "success",
                });
                setTimeout(() => {
                    fetchData();
                    fetchButtonList();
                    handleClose();
                }, 1000);
            } else if (response.status === 409) {
                setMessage({
                    open: true,
                    text: response.message || 'shipping planning already exists ',
                    type: 'error',
                });
            }
            else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    const [formData] = useState({
        clientCompanyId: clientCompanyId,
        commanShipmentId: commanShipmentId,
        shipmentOrgId: shipmentOrgId,
        shipmentId: shipmentId,
        baseCurrencyId: baseCurrencyId,
        currencyId: currencyId,
        conversionRate: '',
        proformaInvoiceId: '',
        proformaInvExpId: '',
        commercialInvExpId: '',
        commercialInvoiceId: '',
        seaFreight: '',
        clearingAgencyCharge: '',
        freightForwardAgencyCharge: '',
        cfsLocalCharges: '',
        shippingLineCharges: '',
        blCharges: '',
        othersCharges: '',
        dutyCharge: '',
        taxCharge: '',
        transportCharge: ''

    });

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={formData}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Create Planning & Budget")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title=" Save and New"
                                        size="small"
                                        variant="contained"
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={proformaInvoiceList}
                                    fieldLabel="Proforma Invoice"
                                    getOptionLabel={(option) => option.title}
                                    value={proformaInvoiceList.find((option) => option.id === values.proformaInvoiceId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('proformaInvoiceId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Proforma Invoice"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={commercialInvoiceList}
                                    fieldLabel="Commercial Invoice"
                                    getOptionLabel={(option) => option.title}
                                    value={commercialInvoiceList.find((option) => option.id === values.commercialInvoiceId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('commercialInvoiceId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Commercial Invoice"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Select Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.currencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currencyId && !!errors.currencyId}
                                    helperText={touched.currencyId && errors.currencyId}
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}

                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <CustomPrefixSuffixField
                                    prefix={currencySym}
                                    placeholder="Sea Freight"
                                    tagLine="Per Con."
                                    name="seaFreight"
                                    fieldLabel="Sea Freight"
                                    type="number"
                                    value={values.seaFreight}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter CFS Local Charges"
                                    name="cfsLocalCharges"
                                    fieldLabel="CFS/Local Charges"
                                    type="number"
                                    value={values.cfsLocalCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Shipping Line Charges"
                                    name="shippingLineCharges"
                                    fieldLabel="Shipping Line Charges"
                                    type="number"
                                    value={values.shippingLineCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter BL Charges"
                                    name="blCharges"
                                    fieldLabel="BL Charges"

                                    type="number"
                                    value={values.blCharges}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Conversion Rate"
                                    name="conversionRate"
                                    fieldLabel="Conversion Rate"
                                    type="number"
                                    value={values.conversionRate}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Clearing Agency Charge"
                                    name="clearingAgencyCharge"
                                    fieldLabel="Clearing Agency Charge"
                                    type="number"
                                    value={values.clearingAgencyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Freight Forward Agency Charge"
                                    name="freightForwardAgencyCharge"
                                    fieldLabel="Freight Forward Agency Charge"
                                    type="number"
                                    value={values.freightForwardAgencyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Others Charges"
                                    name="othersCharges"
                                    fieldLabel="Others Charges"
                                    type="number"
                                    value={values.othersCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Duty Charge"
                                    name="dutyCharge"
                                    fieldLabel="Duty Charge"
                                    type="number"
                                    value={values.dutyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Tax Charge"
                                    name="taxCharge"
                                    fieldLabel="Tax Charge"
                                    type="number"
                                    value={values.taxCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Transport Charge"
                                    name="transportCharge"
                                    fieldLabel="Transport Charge"
                                    type="number"
                                    value={values.transportCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default CreatePlanningBudgetImport;
